import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiAlertTriangle, FiCpu } from 'react-icons/fi';
import { fetchRecommendations, actionRecommendation } from '../../utils/sentimentApi';

const TYPE_STYLES = {
  sla_breach:  { label: 'SLA Breach',   color: 'var(--sm-red)',    bg: 'var(--sm-red-lt)' },
  bulk_spike:  { label: 'Bulk Spike',   color: 'var(--sm-amber)',  bg: 'var(--sm-amber-lt)' },
  churn_risk:  { label: 'Churn Risk',   color: 'var(--sm-purple)', bg: 'var(--sm-purple-lt)' },
  retention:   { label: 'Retention',    color: 'var(--sm-teal-1)', bg: 'rgba(20,184,166,.12)' },
};

export default function SentimentCopilot({ showToast }) {
  const [recs, setRecs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [actionLoading, setActionLoading] = useState(null); // rec id

  const userEmail = localStorage.getItem('user') || 'admin@savi.com';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchRecommendations();
      setRecs(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (rec, action) => {
    setActionLoading(rec.recommendation_id);
    try {
      await actionRecommendation(rec.recommendation_id, action, userEmail);
      setRecs(prev => prev.map(r =>
        r.recommendation_id === rec.recommendation_id ? { ...r, status: action === 'apply' ? 'applied' : 'dismissed' } : r
      ));
      showToast?.(action === 'apply' ? 'Recommendation applied' : 'Recommendation dismissed', action === 'apply' ? 'success' : 'info');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';

  const pending  = recs.filter(r => r.status === 'pending');
  const actioned = recs.filter(r => r.status !== 'pending');

  return (
    <div>
      <div className="sm-section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiCpu size={18} color="var(--sm-primary)" />
          <h2 className="sm-section-title">AI Co-pilot Recommendations</h2>
        </div>
        <button className="sm-btn sm-btn-ghost" onClick={load}><FiRefreshCw size={14} /></button>
      </div>

      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading recommendations…</span></div>
      ) : recs.length === 0 ? (
        <div className="sm-empty">
          <div className="sm-empty-icon">🤖</div>
          <span>No recommendations right now. All clear!</span>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--sm-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>
                Pending · {pending.length}
              </h3>
              <div className="sm-proposal-grid" style={{ marginBottom: 32 }}>
                {pending.map(rec => {
                  const typeStyle = TYPE_STYLES[rec.recommendation_type] || { label: rec.recommendation_type, color: 'var(--sm-primary)', bg: 'var(--sm-primary-lt)' };
                  const isLoading = actionLoading === rec.recommendation_id;
                  return (
                    <div className="sm-proposal-card" key={rec.recommendation_id} style={{ '--delay': `${pending.indexOf(rec) * 50}ms` }}>
                      <div className="sm-proposal-header">
                        <div>
                          <span className="sm-rec-type" style={{ background: typeStyle.bg, color: typeStyle.color }}>
                            {typeStyle.label}
                          </span>
                          <div className="sm-proposal-title" style={{ marginTop: 8 }}>{rec.title}</div>
                          {rec.customer_id && <div className="sm-proposal-customer">{rec.customer_id}</div>}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--sm-muted)', whiteSpace: 'nowrap' }}>{fmtDate(rec.created_at)}</span>
                      </div>

                      <div className="sm-rec-suggestion">{rec.suggestion}</div>

                      {rec.details && (
                        <div className="sm-proposal-detail">
                          <span>{typeof rec.details === 'string' ? rec.details : JSON.stringify(rec.details)}</span>
                        </div>
                      )}

                      <div className="sm-proposal-actions">
                        <button
                          className="sm-btn sm-btn-ghost"
                          style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                          onClick={() => handleAction(rec, 'dismiss')}
                          disabled={isLoading}
                        >
                          <FiXCircle size={13} /> Dismiss
                        </button>
                        <button
                          className="sm-btn sm-btn-primary"
                          style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                          onClick={() => handleAction(rec, 'apply')}
                          disabled={isLoading}
                        >
                          <FiCheckCircle size={13} /> {isLoading ? 'Applying…' : 'Apply'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {actioned.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--sm-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>
                Actioned · {actioned.length}
              </h3>
              <div className="sm-proposal-grid">
                {actioned.map(rec => {
                  const typeStyle = TYPE_STYLES[rec.recommendation_type] || { label: rec.recommendation_type, color: 'var(--sm-primary)', bg: 'var(--sm-primary-lt)' };
                  return (
                    <div className="sm-proposal-card" key={rec.recommendation_id} style={{ opacity: .65 }}>
                      <div className="sm-proposal-header">
                        <div>
                          <span className="sm-rec-type" style={{ background: typeStyle.bg, color: typeStyle.color }}>{typeStyle.label}</span>
                          <div className="sm-proposal-title" style={{ marginTop: 8 }}>{rec.title}</div>
                        </div>
                        <span className={`sm-badge ${rec.status === 'applied' ? 'sm-badge-resolved' : 'sm-badge-system'}`}>
                          {rec.status}
                        </span>
                      </div>
                      <div className="sm-rec-suggestion">{rec.suggestion}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
